<template>
  <div class="price">
    <Header
      :right-sidebar="true"
      :right-sidebar-title="
        `Редактирование абонемента спортзала &quot;${gymName}&quot;`
      "
    >
      <template #right-sidebar>
        <sidebar-items-group title="Отображать столбцы">
          <fields-picker
            v-model="pickedFields"
            :fields="priceHistoryDisplayableFields"
          />
        </sidebar-items-group>
        <sidebar-items-group title="Фильтры">
          <filters-group
            :fields="priceHistoryFields"
            :items="priceHistory"
            v-model="filteredItems"
          />
        </sidebar-items-group>
        <sidebar-item
          button
          variant="warning"
          @click="deletePrice"
          :disabled="loading"
          v-if="!isDeleted"
        >
          Удалить абонемент
          <template #icon>
            <b-icon icon="x-square" />
          </template>
        </sidebar-item>
        <sidebar-item
          button
          variant="success"
          @click="restorePrice"
          :disabled="loading"
          v-else
        >
          Восстановить абонемент
          <template #icon>
            <b-icon icon="box-arrow-up-right" />
          </template>
        </sidebar-item>
        <sidebar-item
          button
          variant="danger"
          @click="showPurgeConformalModal = true"
          :disabled="loading"
        >
          Удалить абонемент навсегда
          <template #icon>
            <b-icon icon="trash" />
          </template>
        </sidebar-item>
      </template>
    </Header>
    <Main>
      <h3 class="text-center">
        Редактирование абонемента спортзала "{{ gymName.toUpperCase() }}"
      </h3>
      <b-overlay :show="loading">
        <price-form
          v-model="priceActualData"
          v-if="priceActualData !== undefined"
          class="price__form"
        />
      </b-overlay>
    </Main>
    <Main>
      <b-overlay :show="loading">
        <h3 class="text-center">История изменений абонемента</h3>
        <b-table
          selectable
          select-mode="single"
          :responsive="true"
          :items="paintedRows"
          :fields="pickedFields"
          hover
          class="table-rounded"
          empty-text="Записей нет"
        />
      </b-overlay>
    </Main>
    <b-modal
      centered
      title="Окончательное удаление абонемента"
      v-model="showPurgeConformalModal"
      ok-title="Удалить"
      cancel-title="Отмена"
      ok-variant="danger"
      cancel-variant="success"
      @ok="purgePrice"
    >
      <p class="my-4">
        Вы точно хотите удалить этот абонемент?<br />(История изменений
        абонемента будет безвозвратно утеряна)
      </p>
    </b-modal>
    <bottom-overlay v-if="edited">
      <b-button
        @click="updatePrice"
        block
        squared
        variant="success"
        class="price__update-button"
        :disabled="loading"
      >
        Сохранить изменения
      </b-button>
      <b-button
        @click="discardChanges"
        block
        squared
        variant="warning"
        class="price__discard-changes-button"
        :disabled="loading"
      >
        Отменить изменения
      </b-button>
    </bottom-overlay>
  </div>
</template>
<script>
import Header from "@/components/Layout/Header";
import FieldsPicker from "@/components/Tables/FieldsPicker";
import SidebarItemsGroup from "@/components/Layout/Sidebars/SidebarItems/SidebarItemsGroup";
import FiltersGroup from "@/components/Tables/Filters/FiltersGroup";
import SidebarItem from "@/components/Layout/Sidebars/SidebarItems/SidebarItem";
import BottomOverlay from "@/components/BottomOverlay";
import { displayableFields } from "@/components/Tables/defaultFields";
import { priceHistoryFields } from "@/views/Gyms/Prices/models/pricesTableModel";
import PriceModel from "@/components/Forms/Gym/models/priceModel";
import axios from "@/axios";
import Main from "@/components/Layout/Main";
import PriceForm from "@/components/Forms/Gym/Price/PriceForm";
import { mapActions, mapGetters } from "vuex";
import { defaultRowPainter } from "@/components/Tables/rowPainters";
import pageTitleMixin from "@/mixins/pageTitleMixin";
export default {
  components: {
    PriceForm,
    Main,
    BottomOverlay,
    SidebarItem,
    FiltersGroup,
    SidebarItemsGroup,
    FieldsPicker,
    Header
  },
  mixins: [pageTitleMixin],
  props: {
    gymId: {
      type: Number,
      required: true
    },
    priceId: {
      type: Number,
      required: true
    }
  },
  data() {
    let priceHistoryDisplayableFields = displayableFields(priceHistoryFields);
    return {
      priceHistoryFields,
      priceHistoryDisplayableFields,
      filteredItems: [],
      pickedFields: priceHistoryDisplayableFields,
      priceHistory: [],
      priceActualData: undefined,
      loading: true,
      showPurgeConformalModal: false
    };
  },
  computed: {
    ...mapGetters("gyms", ["gyms"]),
    gym: ({ gymId, gyms }) => gyms.find(({ id }) => Number(id) === gymId),
    gymName: ({ gym }) => gym?.name || "",
    isDeleted: ({ priceHistory }) =>
      priceHistory[0] !== undefined
        ? Boolean(Number(priceHistory[0].is_deleted))
        : false,
    edited: ({ priceActualData, priceHistory }) =>
      priceHistory[0] !== undefined &&
      JSON.stringify(priceActualData) !==
        JSON.stringify(new PriceModel(priceHistory[0])),
    paintedRows: ({ filteredItems }) => defaultRowPainter(filteredItems)
  },
  methods: {
    ...mapActions("gyms", ["loadGyms"]),
    async updatePrice() {
      this.loading = true;
      try {
        await axios.patch(`/gyms/prices/${this.priceId}`, this.priceActualData);
        await this.loadData();
      } catch {
        this.loading = false;
      }
    },
    discardChanges() {
      this.priceActualData = new PriceModel(this.priceHistory[0]);
    },
    async deletePrice() {
      this.loading = true;
      await axios.delete(`/gyms/prices/${this.priceId}`);
      await this.loadData();
    },
    async restorePrice() {
      this.loading = true;
      await axios.patch(`/gyms/prices/${this.priceId}/restore`);
      await this.loadData();
    },
    async purgePrice() {
      this.loading = true;
      await axios.delete(`/gyms/prices/${this.priceId}/purge`);
      await this.$router.push(`/gyms/${this.gymId}/prices`);
    },
    async loadData() {
      this.priceHistory = (
        await axios.get(`/gyms/prices/${this.priceId}/history`)
      ).data.gym_price_history;
      this.priceActualData = new PriceModel(this.priceHistory[0]);
      this.loading = false;
      await this.loadGyms();
    }
  },
  async created() {
    await this.loadData();
  }
};
</script>

<style lang="scss">
.price {
  &__form {
    padding: 0 5px;
  }
}
</style>
